import React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PricingFeature {
  name: string;
}

type ButtonType = 'subscribe' | 'upgrade' | 'downgrade' | 'saveWithYearly' | 'current';

interface PricingCardProps {
  productName: string;
  description: string;
  price: string;
  monthlyPrice?: string;
  buttonText: string;
  features: (PricingFeature | string)[];
  onSubscribe: () => void;
  isLoading?: boolean;
  billingPeriod: 'monthly' | 'yearly';
  isCurrentPlan?: boolean;
  buttonType?: ButtonType;
  yearlyAmount?: number;
  monthlyAmount?: number;
  currency?: string;
  isPopular?: boolean;
}

export const PricingCard: React.FC<PricingCardProps> = ({
  productName,
  description,
  price,
  monthlyPrice,
  buttonText,
  features,
  onSubscribe,
  isLoading = false,
  billingPeriod,
  isCurrentPlan = false,
  buttonType = 'subscribe',
  yearlyAmount,
  monthlyAmount,
  currency,
  isPopular = false
}) => {
  const { t } = useTranslation();

  const getButtonText = () => {
    switch (buttonType) {
      case 'current':
        return t('billing.manage', 'Manage');
      case 'upgrade':
        return t('billing.upgrade');
      case 'downgrade':
        return t('billing.downgrade');
      case 'saveWithYearly':
        return t('billing.saveWithYearly');
      default:
        return buttonText;
    }
  };

  const getButtonStyle = () => {
    switch (buttonType) {
      case 'current':
        return 'bg-green-600 text-white hover:bg-green-700';
      case 'upgrade':
        return 'bg-primary-600 text-white hover:bg-primary-700';
      case 'downgrade':
        return 'bg-gray-500 text-white hover:bg-gray-600';
      case 'saveWithYearly':
        return 'bg-green-600 text-white hover:bg-green-700';
      default:
        return 'bg-primary-600 text-white hover:bg-primary-700';
    }
  };

  // Calculate savings for yearly plans
  const getSavingsInfo = () => {
    if (billingPeriod !== 'yearly') {
      return null;
    }

    const parseCents = (formatted?: string): number | undefined => {
      if (!formatted) return undefined;
      // Remove currency symbols and spaces, handle both , and . as decimal/thousand separators
      const cleaned = formatted.replace(/[^0-9,\.]/g, '').replace(/\s/g, '');
      if (!cleaned) return undefined;
      // If both separators present, assume last one is decimal
      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');
      let normalized = cleaned;
      if (lastComma > -1 && lastDot > -1) {
        if (lastComma > lastDot) {
          normalized = cleaned.replace(/\./g, '').replace(',', '.');
        } else {
          normalized = cleaned.replace(/,/g, '');
        }
      } else if (lastComma > -1) {
        normalized = cleaned.replace(',', '.');
      } else {
        normalized = cleaned;
      }
      const num = Number.parseFloat(normalized);
      if (Number.isNaN(num)) return undefined;
      return Math.round(num * 100);
    };

    const derivedCurrency = (currency || 'EUR').toUpperCase();
    const yearlyTotal = yearlyAmount ?? parseCents(price);
    const monthlyCents = monthlyAmount ?? parseCents(monthlyPrice);
    if (!yearlyTotal || !monthlyCents) return null;

    const monthlyYearlyTotal = monthlyCents * 12;
    const rawSavings = monthlyYearlyTotal - yearlyTotal;
    const savingsAmount = rawSavings > 0 ? rawSavings : 0; // show 0 if no savings

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: derivedCurrency,
        minimumFractionDigits: 0
      }).format(amount / 100);
    };

    return {
      savingsAmount: formatCurrency(savingsAmount),
      monthlyYearlyTotal: formatCurrency(monthlyYearlyTotal)
    };
  };

  const savingsInfo = getSavingsInfo();
  return (
    <div className={`relative rounded-2xl border p-6 ${
      buttonType === 'current'
        ? 'bg-green-50 border-green-500 border-2' 
        : isPopular
          ? 'bg-primary-50 border-primary-500 border-2'
          : 'bg-white border-black'
    }`}>
      {isPopular && (
        <div className="absolute -top-3 right-4">
          <span className="inline-block px-3 py-1 bg-primary-600 text-white text-xs font-medium rounded-full">
            {t('billing.mostPopular', 'Most Popular')}
          </span>
        </div>
      )}
      <div className="absolute -top-3 left-4">
        <span className={`inline-block px-4 py-1 text-xs font-medium rounded-full font-title uppercase tracking-wide ${
          buttonType === 'current'
            ? 'bg-purple-100 text-purple-700' 
            : 'bg-orange-100 text-orange-700'
        }`}>
          {productName}
        </span>
      </div>
      {buttonType === 'current' && (
        <div className="absolute -top-3 right-4">
          <span className="inline-block px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-full">
            {t('billing.currentPlanBadge')}
          </span>
        </div>
      )}
      <div className="mb-6">
        {billingPeriod === 'yearly' ? (
          <>
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold">{monthlyPrice}</span>
                <span className="text-gray-500">{t('billing.perMonth', { defaultValue: '/mo' })}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {t('billing.billedAnnually', { defaultValue: 'Billed annually at' })} {price}
              </p>
              {savingsInfo && (
                <div
                  className="mt-3 inline-flex items-center px-4 py-1.5 rounded-lg font-bold text-white whitespace-nowrap"
                  style={{ backgroundColor: '#17a34a' }}
                >
                  {buttonType === 'current'
                    ? t('billing.savedAmount', '{{amount}} saved', { amount: savingsInfo.savingsAmount.replace(/\s/g, '').replace(',00', '') })
                    : t('billing.saveAmount', 'Save {{amount}}', { amount: savingsInfo.savingsAmount.replace(/\s/g, '').replace(',00', '') })}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold">{price}</span>
            <span className="text-gray-500">{t('billing.perMonth', { defaultValue: '/mo' })}</span>
          </div>
        )}
      </div>
      <h3 className="text-xl font-semibold mb-2 font-title uppercase tracking-wide">{productName}</h3>
      <p className="text-sm text-gray-600 mb-6">{description}</p>
      <button
        onClick={onSubscribe}
        disabled={isLoading}
        className={`w-full py-2 px-4 rounded-lg transition-colors mb-6 disabled:opacity-50 disabled:cursor-not-allowed ${getButtonStyle()}`}
      >
        {isLoading ? t('common.loading', 'Loading...') : getButtonText()}
      </button>
      <ul className="space-y-4">
        {features?.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <span className="text-gray-600">{typeof feature === 'string' ? feature : feature.name}</span>
          </li>
        )) || []}
      </ul>
    </div>
  );
};